import type { Input } from "../../../api/call";
import type { ParsingError } from "../types";
import * as io from "io-ts";
import { DateFromISOString } from "io-ts-types";

export enum ConverterErrorType {
  INVALID_LABEL_TOO_LONG = "INVALID_LABEL_TOO_LONG",
  INVALID_GROUP_TOO_LONG = "INVALID_GROUP_TOO_LONG",
  /** Occure when a snapshot has no date */
  MISSING_SNAPSHOT_DATE = "MISSING_SNAPSHOT_DATE",
  /** Occure when two snapshots on the same date diverge in acquisition date or label or group */
  SNAPSHOT_METADATA_MISSMATCH = "SNAPSHOT_METADATA_MISSMATCH",
  /** Occure when a sensor value is not a number */
  INVALID_SENSOR_VALUE = "INVALID_SENSOR_VALUE",
  INVALID_SENSOR_VALUE_MISSING_MEAN = "INVALID_SENSOR_VALUE_MISSING_MEAN",
  INVALID_SOURCE_ID = "INVALID_SOURCE_ID",
  /** Occure when file shares different sourceId */
  CONFLICTING_SOURCE_ID = "CONFLICTING_SOURCE_ID",
  /** Occure when file shares different values for a given snapshot */
  CONFLICTING_SENSOR_VALUE = "CONFLICTING_SENSOR_VALUE",
  DUPLICATED_SNAPSHOT_DATE = "DUPLICATED_SNAPSHOT_DATE",
  /** JSON IMPORT */
  MALFORMED_JSON = "MALFORMED_JSON",
  INVALID_JSON = "INVALID_JSON",
  MISSING_SOURCE_ID = "MISSING_SOURCE_ID",
  /** CSV, CONTEXT File */
  EMPTY_DATA_EXTRACTION = "EMPTY_FILE",
}

type ConverterErrorInfo<T> = T extends "INVALID_SENSOR_VALUE"
  ? { snapshotDate?: string; sensorTag: string }
  : T extends "CONFLICTING_SENSOR_VALUE"
    ? {
        snapshotDate?: string;
        sensorTag: string;
        mean?: [number, number];
        std?: [number, number];
      }
    : T extends "CONFLICTING_SOURCE_ID"
      ? {
          sourceId: [string, string];
        }
      : T extends "MISSING_SNAPSHOT_DATE"
        ? {
            snapshotIndex: number;
          }
        : T extends "SNAPSHOT_METADATA_MISSMATCH"
          ? {
              snapshotDate?: string;
              label?: [string, string];
              group?: [string, string];
              acquisitionStartDate?: [string, string];
              acquisitionEndDate?: [string, string];
            }
          : never;

export type ConverterError<T> = {
  kind: T;
  explain?: string;
  data?: ConverterErrorInfo<T>;
};

export type GenericConverterErrors = (
  | ConverterError<ConverterErrorType.INVALID_SENSOR_VALUE>
  | ConverterError<ConverterErrorType.CONFLICTING_SENSOR_VALUE>
  | ConverterError<ConverterErrorType.CONFLICTING_SOURCE_ID>
  | ConverterError<ConverterErrorType.MISSING_SNAPSHOT_DATE>
  | ConverterError<ConverterErrorType.SNAPSHOT_METADATA_MISSMATCH>
)[];

export type ConverterSensorValueError = ConverterError<ConverterErrorType.INVALID_SENSOR_VALUE>;

type Payload = Input<"PATCH", "/sources/snapshots">["body"];

export interface ConverterResult {
  sourceId: string | undefined;
  snapshots: {
    date: string;
    label?: string;
    group?: string;
    acquisitionStartDate?: string;
    acquisitionEndDate?: string;
    sensorValues: Payload["snapshots"][number]["sensorValues"];
  }[];
}
/** @note : if the API contract changes on the Payload, the io-ts ConverterResultIOTS typing must be updated. */
export const ConverterResultIOTS = io.strict({
  sourceId: io.union([io.string, io.undefined]),
  snapshots: io.array(
    io.strict({
      date: DateFromISOString,
      label: io.union([io.string, io.undefined]),
      group: io.union([io.string, io.undefined]),
      acquisitionStartDate: io.union([DateFromISOString, io.undefined]),
      acquisitionEndDate: io.union([DateFromISOString, io.undefined]),
      sensorValues: io.array(
        io.strict({
          sensorTag: io.string,
          mean: io.number,
          std: io.union([io.number, io.undefined]),
          skewness: io.union([io.number, io.undefined]),
          rawValuesCount: io.union([io.number, io.undefined]),
        }),
      ),
    }),
  ),
});

export interface ConverterOutput {
  result: ConverterResult;
  errors: ParsingError<ConverterErrorType>[];
}

export type Converter = (input: string) => ConverterOutput;
